import ContactActions from 'common/redux/actions/contact';
import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import { Contact, ContactType } from 'common/models/internal/Contact';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import { contact as contactApi } from 'common/middleware/api';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';

import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import ContactNumberInputList from 'common/entity/contactMaster/ContactNumberInputList';
import { PhoneNumber } from 'common/models/internal/PhoneNumber';
import { EmailAddress } from 'common/models/internal/EmailAddress';
import { ENTITY_NAME_ID } from 'common/constants/entity/contactMaster';

const CONTACT_FIELDS = [
    {
        accessor: 'code',
        label: 'contactForm.code',
        helpText: 'contactForm.codeTooltip',
        placeholder: 'contactForm.codePlaceholder',
        halfWidth: false,
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.get('id'),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            }
            if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'contactForm.name',
        placeholder: 'contactForm.namePlaceholder',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'type',
        type: FieldTypeEnum.Select,
        label: 'contactForm.type',
        placeholder: 'contactForm.typePlaceholder',
        required: true,
        options: ContactType.getOptions({
            translationKey: 'contactProperties.contactType'
        }),
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'company',
        label: 'contactForm.company',
        placeholder: 'contactForm.companyPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'teams',
        label: 'contactForm.teams',
        placeholder: 'contactForm.teamsPlaceHolder',
        type: FieldTypeEnum.Teams,
        helpText: 'contactForm.teamsTooltip'
    },
    {
        accessor: 'phoneNumber.phoneNumber',
        label: 'contactForm.primaryMobileNumber',
        required: true,
        type: FieldTypeEnum.Phone
    },
    {
        accessor: 'additionalPhoneNumbers',
        label: 'contactForm.alternateMobileNumber',
        onChangeHandler: ({ entity, field, fieldIndex, value }) => {
            return entity.setIn(
                [...field.absoluteAccessor, fieldIndex],
                PhoneNumber.fromJS({ phoneNumber: value })
            );
        },
        supportsOnDelete: true,
        onDeleteHandler: ({ entity, fieldIndex, field }) => {
            return entity.deleteIn([...field.absoluteAccessor, fieldIndex]);
        },
        extraProps: ({ field, entity }) => {
            return {
                value: entity.getIn([...field.absoluteAccessor])
            };
        },
        component: ContactNumberInputList
    },
    {
        accessor: 'landlineNumber.phoneNumber',
        label: 'contactForm.landlineNumber',
        type: FieldTypeEnum.Phone,
        extraProps: ({ field, entity }) => {
            return {
                value: entity.getIn(field.absoluteAccessor)
            };
        }
    },
    {
        accessor: 'emailAddress.emailAddress',
        label: 'contactForm.primaryEmailAddress',
        placeholder: 'contactForm.primaryEmailAddressPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'additionalEmailAddresses',
        label: 'contactForm.alternateEmailAddress',
        type: FieldTypeEnum.Text,
        placeholder: 'contactForm.alternateEmailAddressPlaceholder',
        onChangeHandler: ({ field, value, entity }) => {
            return entity.setIn(
                [...field.absoluteAccessor, 0],
                EmailAddress.fromJS({ emailAddress: value })
            );
        },
        extraProps: ({ field, entity }) => {
            return {
                value: entity.getIn([
                    ...field.absoluteAccessor,
                    0,
                    'emailAddress'
                ])
            };
        }
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'contactSettings'
            };
        }
    }
];

const SingleContactEditContainer = entityMasterSingleEditFactory({
    MasterEntity: Contact,
    MasterActions: ContactActions,
    EntityFetchAPI: contactApi,
    FIELDS: CONTACT_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'contactId',
        ENTITY_SETTINGS_NAME: 'contactSettings'
    },
    mapEntityStateToProps: ({ state, props }) => {
        const teams = teamsSelector(state, props);
        const countryISOCode = ClientSelectors.getCountryISOCodeFromLocaleSettings(
            state,
            props
        );
        return {
            teams,
            defaultCountryISOCode: countryISOCode
        };
    }
});

export default SingleContactEditContainer;
